'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'

import { CategoryFilterNav } from '@/components/CategoryFilterNav'
import { scrollExpandedAccordionItemOnMobile } from '@/components/FilterAccordionSection/mobileAccordionScroll'
import RichText from '@/components/RichText'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { FAQ_ALL_CATEGORY, type FaqItemData, type FaqSectionData } from '@/lib/faqs/faqData'
import { useMessages } from '@/lib/i18n/useMessages'
import { cn } from '@/utilities/ui'

export type FaqAccordionProps = {
  accordionItemGap?: 'default' | 'tight'
  beforeFilters?: React.ReactNode
  categoryFiltersAriaLabel?: string
  className?: string
  faqData: FaqSectionData
  filterLayoutId?: string
  initialVisibleCount?: number
  intro?: readonly string[]
  scrollExpandedItemOnMobile?: boolean
}

export function getFaqItemValue(item: FaqItemData) {
  return `faq-${item.id}`
}

export const FaqAccordion: React.FC<FaqAccordionProps> = ({
  accordionItemGap = 'default',
  beforeFilters,
  categoryFiltersAriaLabel,
  className,
  faqData,
  filterLayoutId = 'faq-category-indicator',
  initialVisibleCount,
  intro,
  scrollExpandedItemOnMobile = true,
}) => {
  const messages = useMessages()
  const copy = messages.landing.faq
  const [activeCategory, setActiveCategory] = useState<string>(FAQ_ALL_CATEGORY)
  const [expandedValue, setExpandedValue] = useState<string | undefined>(
    faqData.items[0] ? getFaqItemValue(faqData.items[0]) : undefined,
  )
  const [showAllFaqs, setShowAllFaqs] = useState(false)
  const scrollAbortRef = useRef<AbortController | null>(null)

  const filteredItems = useMemo(() => {
    if (activeCategory === FAQ_ALL_CATEGORY) return faqData.items
    return faqData.items.filter((item) => item.category === activeCategory)
  }, [activeCategory, faqData.items])

  const handleCategoryChange = (category: string) => {
    scrollAbortRef.current?.abort()
    scrollAbortRef.current = null
    setActiveCategory(category)
    setShowAllFaqs(false)
    const nextItems =
      category === FAQ_ALL_CATEGORY
        ? faqData.items
        : faqData.items.filter((item) => item.category === category)
    setExpandedValue(nextItems[0] ? getFaqItemValue(nextItems[0]) : undefined)
  }

  const handleAccordionChange = (value: string) => {
    const closingId = expandedValue

    scrollAbortRef.current?.abort()
    scrollAbortRef.current = null

    setExpandedValue(value || undefined)

    if (!scrollExpandedItemOnMobile || !value) return

    const controller = new AbortController()
    scrollAbortRef.current = controller

    void scrollExpandedAccordionItemOnMobile({
      closeItemId: closingId,
      openItemId: value,
      signal: controller.signal,
    }).catch(() => {
      // Ignore aborted scroll requests from rapid accordion changes.
    })
  }

  useEffect(() => {
    return () => {
      scrollAbortRef.current?.abort()
    }
  }, [])

  if (faqData.items.length === 0) {
    return null
  }

  const realCategories = faqData.categoryOrder.filter(
    (category) => category !== FAQ_ALL_CATEGORY,
  )
  const showCategoryFilters = realCategories.length > 1

  const useVisibleCap =
    initialVisibleCount !== undefined && initialVisibleCount > 0 && filteredItems.length > initialVisibleCount
  const hiddenFaqCount = useVisibleCap
    ? Math.max(0, filteredItems.length - initialVisibleCount)
    : 0
  const visibleItems =
    useVisibleCap && !showAllFaqs
      ? filteredItems.slice(0, initialVisibleCount)
      : filteredItems

  const introContent =
    beforeFilters ??
    (intro && intro.length > 0 ? (
      <div className="mb-8 font-outfit text-base leading-relaxed text-dark-800/80 lg:mb-10">
        {intro.map((paragraph, index) => (
          <p key={index} className={index > 0 ? 'mt-4' : undefined}>
            {paragraph}
          </p>
        ))}
      </div>
    ) : null)

  return (
    <div className={cn('min-w-0', className)}>
      {introContent}

      {showCategoryFilters ? (
        <CategoryFilterNav
          activeCategory={activeCategory}
          ariaLabel={categoryFiltersAriaLabel ?? copy.categoryFiltersAriaLabel}
          categoryLabels={faqData.categoryLabels}
          categoryOrder={[...faqData.categoryOrder]}
          className="mb-6 lg:mb-8"
          desktopAlign="start"
          desktopWidth="fit"
          layoutId={filterLayoutId}
          onSelect={handleCategoryChange}
        />
      ) : null}

      <Accordion
        className={accordionItemGap === 'tight' ? 'gap-2' : 'gap-4'}
        collapsible
        onValueChange={handleAccordionChange}
        type="single"
        value={expandedValue}
        variant="faq"
      >
        {visibleItems.map((item) => (
          <AccordionItem
            key={item.id}
            id={getFaqItemValue(item)}
            value={getFaqItemValue(item)}
          >
            <AccordionTrigger>{item.question}</AccordionTrigger>
            <AccordionContent>
              <RichText
                className="pt-8 font-outfit text-sm leading-5 text-dark-800/80 [&_a]:underline [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p+p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-5"
                data={item.answer}
                enableGutter={false}
                enableProse={false}
              />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {hiddenFaqCount > 0 && !showAllFaqs ? (
        <div className="mt-8 flex justify-center lg:mt-4 lg:justify-start">
          <Button onClick={() => setShowAllFaqs(true)} type="button" variant="outline">
            {hiddenFaqCount === 1
              ? copy.showMoreQuestion
              : copy.showMoreQuestions.replace('{count}', String(hiddenFaqCount))}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
